import { KvkkSaklama } from "./KvkkSaklama";
import { useState } from "react";
import { KagitVeliOnayi } from "./KagitVeliOnayi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  Iskelet,
  Kart,
  MetinAlani,
  Rozet,
  Secim,
  Sekmeler,
  cn,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  basvuruAc,
  basvuruSonuclandir,
  basvurular,
  metinGetir,
  metinKarsilastir,
  metinSurumleri,
  metinYayinla,
  rizaGeriCek,
  rizaVer,
  rizalarim,
  type Basvuru,
  type HukukiMetin,
  type BasvuruTuru,
  type RizaDurumu,
  type RizaTuru,
} from "./api";

/**
 * KVKK — riza, surumlu metin, veri sahibi basvurusu (F-LGL-02)
 */

const RIZA_ADI: Record<RizaTuru, string> = {
  PRIVACY_NOTICE: "Aydınlatma metni",
  PARENT_GUARANTEE: "Veli muvafakati",
  DISTANCE_SALES: "Mesafeli satış sözleşmesi",
  PRE_INFO_FORM: "Ön bilgilendirme formu",
  WITHDRAWAL_RIGHT: "Cayma hakkı bilgilendirmesi",
  DATA_TRANSFER_ABROAD: "Yurt dışına aktarım",
  CONTACT_SMS: "SMS ile bilgilendirme",
  CONTACT_WHATSAPP: "WhatsApp ile bilgilendirme",
  CONTACT_EMAIL: "E-posta ile bilgilendirme",
  MARKETING: "Kampanya ve tanıtım iletisi",
  PROTECTED_NOTES: "Korumalı danışmanlık notu",
};

const BASVURU_ADI: Record<BasvuruTuru, string> = {
  ACCESS: "Bilgi talebi",
  RECTIFICATION: "Düzeltme",
  ERASURE: "Silme",
  PORTABILITY: "Veri taşınabilirliği",
  OBJECTION: "İşlemeye itiraz",
  RESTRICTION: "İşlemenin sınırlandırılması",
};

const DURUM_ADI: Record<Basvuru["status"], string> = {
  RECEIVED: "Alındı",
  IN_PROGRESS: "İnceleniyor",
  COMPLETED: "Sonuçlandı",
  REJECTED: "Reddedildi",
};

/**
 * GERI CEKILEMEYEN rizalar — sunucudaki listenin AYNISI.
 *
 * Ekran yine de sunucunun cevabina bakiyor: burada tutmasinin tek
 * sebebi, basilinca hata alinacak bir dugmeyi hic gostermemek.
 * Liste ayrisirsa sunucu son sozu soyluyor ve kullanici hata
 * mesajini goruyor — sessizce yanlis davranmiyor.
 */
const GERI_CEKILEMEZ: RizaTuru[] = [
  "PRIVACY_NOTICE",
  "DISTANCE_SALES",
  "PRE_INFO_FORM",
  "PARENT_GUARANTEE",
  "WITHDRAWAL_RIGHT",
];

function tarih(iso: string): string {
  return new Date(iso).toLocaleDateString("tr-TR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * @param metinYonetebilir Kullanicinin `kvkk.metin.yonet` izni var mi.
 *
 * Menu suzmesiyle AYNI yontem (bkz. `Uygulama.tsx` MENU): sunucunun
 * COZDUGU izin kumesinden okunuyor, ekran kendi karar vermiyor. Bu bir
 * guvenlik siniri DEGIL — asil kontrol her istekte sunucuda. Amaci,
 * kullaniciya basinca 403 alacagi bir sekme gostermemek.
 */
type KvkkSekmesi = "rizalarim" | "basvurular" | "metinler" | "kagit" | "saklama";

export function Kvkk({
  metinYonetebilir = false,
  sonuclandirabilir = false,
  kagitKaydedebilir = false,
  saklamaYonetebilir = false,
}: {
  metinYonetebilir?: boolean;
  sonuclandirabilir?: boolean;
  rizaYonetebilir?: boolean;
  kagitKaydedebilir?: boolean;
  /** F-LGL-06 — `sistem.kayit.kalici_sil` (kısıtlı, sudo). */
  saklamaYonetebilir?: boolean;
}) {
  const [gorunum, setGorunum] = useState<KvkkSekmesi>("rizalarim");

  // Tip ACIKCA yaziliyor: `as const` ile yayilim birlestiginde
  // TypeScript literal birlesimi kaybedip `string`e dusuruyor.
  const sekmeler: Array<[KvkkSekmesi, string]> = [
    ["rizalarim", "Onaylarım"],
    ["basvurular", "Başvurular"],
    ...(kagitKaydedebilir ? ([["kagit", "Kâğıt veli onayı"]] as Array<[KvkkSekmesi, string]>) : []),
    ...(metinYonetebilir ? ([["metinler", "Metinler"]] as Array<[KvkkSekmesi, string]>) : []),
    ...(saklamaYonetebilir ? ([["saklama", "Saklama"]] as Array<[KvkkSekmesi, string]>) : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      {/*
        SEKME DEĞİL, SÜZGEÇ DÜĞMELERİ — fark ekran okuyucuda.

        Elle yazılmış `role="tab"` şeridi vardı: `aria-selected` koyulmuş
        ama ok tuşlarıyla gezinme, gezinen odak ve `tabpanel` bağı YOKTU.
        Eksik kurulmuş bir ARIA deseni hiç yazmamaktan KÖTÜ — kullanıcıya
        tutulmayan bir söz veriyor.
      */}
      <Sekmeler
        etiket="KVKK görünümü"
        secili={gorunum}
        onSec={setGorunum}
        sekmeler={sekmeler.map(([deger, etiket]) => ({ deger, etiket }))}
      />

      {gorunum === "rizalarim" && <Rizalarim />}
      {gorunum === "basvurular" && <Basvurular sonuclandirabilir={sonuclandirabilir} />}
      {gorunum === "metinler" && metinYonetebilir && <Metinler />}
      {gorunum === "kagit" && kagitKaydedebilir && <KagitVeliOnayi />}
      {gorunum === "saklama" && saklamaYonetebilir && <KvkkSaklama />}
    </div>
  );
}

// ---------------------------------------------------------------
// ONAYLARIM
// ---------------------------------------------------------------

function Rizalarim() {
  const istemci = useQueryClient();

  const { data, isPending, isError, error, refetch } = useQuery<RizaDurumu>({
    queryKey: ["rizalarim"],
    queryFn: rizalarim,
    refetchOnWindowFocus: false,
  });

  const [gosterilen, setGosterilen] = useState<Record<string, string>>({});
  const [yenidenOkunacak, setYenidenOkunacak] = useState<RizaTuru | null>(null);
  const [acikMetin, setAcikMetin] = useState<HukukiMetin | null>(null);
  const [metinHatasi, setMetinHatasi] = useState<string | null>(null);
  const [metinYukleniyor, setMetinYukleniyor] = useState(false);
  const metniAc = async (tur: RizaTuru) => {
    setMetinYukleniyor(true);
    try {
      const metin = await metinGetir(tur);
      istemci.setQueryData<RizaDurumu>(["rizalarim"], (eski) =>
        eski
          ? {
              ...eski,
              yururluktekiSurumler: { ...eski.yururluktekiSurumler, [tur]: metin.version },
              rizalar: eski.rizalar.map((r) =>
                r.type === tur && eski.yururluktekiSurumler[tur] !== metin.version
                  ? { ...r, guncelMi: false }
                  : r,
              ),
            }
          : eski,
      );
      setAcikMetin(metin);
      setGosterilen((eski) => ({ ...eski, [`${data!.userId}:${tur}`]: metin.version }));
      setYenidenOkunacak((eski) => (eski === tur ? null : eski));
    } catch (err) {
      setMetinHatasi(err instanceof Error ? err.message : "Metin alınamadı.");
    } finally {
      setMetinYukleniyor(false);
    }
  };
  const verMut = useMutation({
    mutationFn: ({ tur, surum }: { tur: RizaTuru; surum: string }) => rizaVer(tur, surum),
    retry: false,
    onError: async (err, { tur }) => {
      if (err instanceof ApiHatasi && err.durum === 409 && err.kod === "METIN_DEGISTI") {
        setAcikMetin(null);
        setYenidenOkunacak(tur);
        await refetch();
        await metniAc(tur);
        setMetinHatasi("Metin güncellendi, yeniden okuyun ve yeniden onaylayın.");
      }
    },
    onSuccess: async () => {
      setAcikMetin(null);
      await istemci.invalidateQueries({ queryKey: ["rizalarim"] });
      await istemci.invalidateQueries({ queryKey: ["ben"] });
    },
  });

  const cekMut = useMutation({
    mutationFn: (tur: RizaTuru) => rizaGeriCek(tur),
    onSuccess: async () => {
      setAcikMetin(null);
      await istemci.invalidateQueries({ queryKey: ["rizalarim"] });
      await istemci.invalidateQueries({ queryKey: ["ben"] });
    },
  });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Onaylar alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  const verilen = new Map(data.rizalar.map((r) => [r.type, r]));
  const turler = Object.keys(data.yururluktekiSurumler) as RizaTuru[];
  const engelliyor = new Set<RizaTuru>(data.engelleyenEksikler);
  // Sunucu alanı aktivasyonu değil kullanım etkisini bildirir.
  const hesabiEtkiler = data.rizaHesabiEtkiler !== false;
  /*
    `eksikIstegeBagli` sunucudan geliyordu ve HİÇBİR EKRAN OKUMUYORDU —
    bu depoda tanıdık sınıf. Alanın tek amacı zaten bu ayrımı
    yapabilmekti.
  */
  const istegeBagliEksik = new Set<RizaTuru>(data.eksikIstegeBagli);

  /*
    ================================================================
    ZORUNLU / İSTEĞE BAĞLI AYRIMI — SUNUCUDAN, KOPYA DEĞİL
    ================================================================
    `zorunluTurler` bu ayrımı **verilmiş** rızalarda da mümkün kılıyor;
    önce yalnızca eksik olanların sınıfı biliniyordu.

    Liste panelde ÜRETİLMİYOR, okunuyor. `ZORUNLU_RIZALAR`
    04.09.2026'da değişti (`DATA_TRANSFER_ABROAD` isteğe bağlıdan
    zorunluya geçti) — bir kopya olsaydı **o gün** sessizce yanlış
    olurdu. Teorik bir risk değil, gerçekleşmiş bir olayın tekrarı.
  */
  const zorunlu = new Set<RizaTuru>(data.zorunluTurler ?? []);
  /*
    SINIFLANDIRMA GELMEDIYSE BOLMUYORUZ.

    `zorunluTurler` bos gelirse her rizanin "istege bagli" kutusuna
    dusmesi gerekirdi — yani ekran, HESABI ENGELLEYEN bir onaya
    "verilmemesi hizmeti etkilemiyor" derdi. Bilmedigimiz bir seyi
    soylemek yerine tek liste ciziyoruz: eksik bir ayrim, YANLIS bir
    ayrimdan iyidir.

    Sunucu bu alani her zaman gonderiyor; bu dal yalnizca alan bir gun
    dusesse diye var. Ama tam da o gun sessiz kalmamasi gerekiyor.
  */
  const bolunebilir = zorunlu.size > 0;
  const zorunluOlanlar = bolunebilir ? turler.filter((t) => zorunlu.has(t)) : turler;
  const istegeBagliOlanlar = bolunebilir ? turler.filter((t) => !zorunlu.has(t)) : [];

  /*
    ================================================================
    SATIR ÇİZİMİ TEK YERDE — İKİ KUTU AYNI ŞEYİ ÇİZİYOR
    ================================================================
    Liste ikiye bölündü ama satırın kendisi bölünmedi. İki ayrı kopya
    olsaydı, birine eklenen bir bilgi (sürüm eskimesi, "veliniz
    onayladı", geri çekilemez sebebi) ötekinde eksik kalırdı — ve
    hangisinde eksik kaldığı ancak o hâle düşünce anlaşılırdı.
  */
  const satir = (tur: RizaTuru) => {
    const kayit = verilen.get(tur);
    const gecerli = kayit?.guncelMi === true;
    const cekilebilir = gecerli && !GERI_CEKILEMEZ.includes(tur);
    // Kendi onayı izne bağlı değildir.
    const kendiVerir = data.rizayiKimVerir === "KENDISI";

    return (
      <Kart key={tur} olarak="li" className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-notr-900 text-sm font-medium">{RIZA_ADI[tur]}</p>
          <p className="text-xs">Sürüm {data.yururluktekiSurumler[tur]}</p>
          <p className="text-notr-600 mt-0.5 text-xs">
            {gecerli ? (
              <>
                {tarih(kayit!.grantedAt)} · sürüm {kayit!.documentVersion}
                {kayit!.veliVerdi ? " · veliniz onayladı" : ""}
              </>
            ) : kayit ? (
              /*
                    Eski surume verilmis onay GECERSIZ. Bunu
                    soylemek gerekiyor: kullanici "ben bunu zaten
                    onaylamistim" diye dusunur ve yeniden
                    onaylamaz.
                  */
              <span className="text-uyari-600">
                Metin güncellendi ({kayit.documentVersion} → {data.yururluktekiSurumler[tur]});
                yeniden onaylamanız gerekiyor
              </span>
            ) : (
              "Henüz onaylanmadı"
            )}
          </p>

          {/*
                ================================================================
                "ONAYSIZ" TEK BAŞINA HİÇBİR ŞEY SÖYLEMİYORDU
                ================================================================
                Liste düzdü: hesabın açılmasını **engelleyen** bir
                onay ile verilmediğinde hiçbir şey değişmeyen bir
                onay, aynı gri "Onaysız" rozetiyle yan yana
                duruyordu. Engelleyenlerin adı yalnızca en üstteki
                kırmızı kutuda geçiyordu — yani okuyan kişi, iki
                ayrı yerdeki iki listeyi ad ad karşılaştırmak
                zorundaydı.

                Bunu bir veli okuyor ve sorusu tek: *hangisini
                yaparsam çocuğumun hesabı açılır.* Cevap satırın
                kendisinde olmalı.

                Sınıflandırma SUNUCUDAN geliyor
                (`engelleyenEksikler` / `eksikIstegeBagli`). Panelde
                zorunlu/isteğe bağlı listesi TUTULMUYOR: o liste
                04.09.2026'da değişti (`DATA_TRANSFER_ABROAD`
                isteğe bağlıdan zorunluya geçti) ve bir kopya o gün
                sessizce yanlış olurdu.
              */}
          {/* Onaylayamayana "kullanım açılmaz" demiyoruz — üst kutuyla aynı gerekçe. */}
          {hesabiEtkiler && engelliyor.has(tur) && (
            <p className="text-hata-700 mt-0.5 text-xs font-medium">
              Onaylanmadan kullanım açılmaz
            </p>
          )}
          {istegeBagliEksik.has(tur) && (
            <p className="text-notr-600 mt-0.5 text-xs">
              İsteğe bağlı — verilmemesi hizmeti etkilemiyor
            </p>
          )}
        </div>

        <span
          className={cn(
            "rounded-alan shrink-0 px-2.5 py-0.5 text-xs font-medium",
            gecerli ? "bg-basarili-500/15 text-onay-700" : "bg-notr-100 text-notr-600",
          )}
        >
          {gecerli ? "Onaylı" : "Onaysız"}
        </span>

        {/*
              ================================================================
              ONAYLAMADAN ÖNCE METNİ OKUYABİLMEK
              ================================================================
              `GET /kvkk/metinler/:tur` yazılıydı ama hiçbir ekran
              çağırmıyordu: kullanıcı ONAYLADIĞI metnin gövdesini
              hiçbir yerde göremiyordu. Okumadan verilen onay
              KVKK anlamında geçerli bir aydınlatma sayılmaz.

              Uç `@KimlikGerekli()` — izin aranmıyor, çünkü
              kişinin onayladığı metni okuması bir yetki değil
              hakkın kendisi.
            */}
        <Buton gorunum="sessiz" onClick={() => void metniAc(tur)} disabled={metinYukleniyor}>
          Metni oku
        </Buton>

        {kendiVerir && !gecerli && (
          <Buton
            onClick={() => {
              setMetinHatasi(null);
              if (gosterilen[`${data.userId}:${tur}`] !== data.yururluktekiSurumler[tur]) {
                setYenidenOkunacak(tur);
                void metniAc(tur);
                setMetinHatasi("Metin güncellendi, yeniden okuyun ve yeniden onaylayın.");
                return;
              }
              verMut.mutate({ tur, surum: gosterilen[`${data.userId}:${tur}`]! });
            }}
            disabled={
              verMut.isPending ||
              metinYukleniyor ||
              yenidenOkunacak === tur ||
              !gosterilen[`${data.userId}:${tur}`]
            }
          >
            Onaylıyorum
          </Buton>
        )}

        {kendiVerir && cekilebilir && (
          <Buton gorunum="sessiz" onClick={() => cekMut.mutate(tur)} disabled={cekMut.isPending}>
            Geri çek
          </Buton>
        )}

        {/*
              Geri cekilemeyen onaylarda dugme yerine SEBEP.
              Dugmenin yoklugu tek basina "sistem bozuk" gibi
              okunur.
            */}
        {kendiVerir && gecerli && !cekilebilir && (
          <span className="text-notr-600 text-xs">Geri çekilemez</span>
        )}
      </Kart>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {metinHatasi && <p role="alert">{metinHatasi}</p>}
      {acikMetin && (
        <section aria-label="Sözleşme metni" className="rounded-alan bg-notr-50 p-4">
          <h2>
            {acikMetin.title} — sürüm {acikMetin.version}
          </h2>
          <p className="whitespace-pre-wrap">{acikMetin.body}</p>
          <Buton gorunum="sessiz" onClick={() => setAcikMetin(null)}>
            Kapat
          </Buton>
        </section>
      )}
      {/*
        ================================================================
        RIZAYI KIM VERIR — SUNUCUDAN
        ================================================================
        Ekran yas hesabi YAPMIYOR. Yapsaydi saati yanlis olan bir
        cihazda 18'ini yeni dolduran ogrenci hala "velin versin"
        gorurdu; ya da tersi.
      */}
      {data.rizayiKimVerir === "VELI" && (
        <p className="rounded-alan bg-uyari-500/10 text-notr-800 p-3 text-sm">
          18 yaşından küçük olduğunuz için onayları <strong>veliniz</strong> verir. Burada yalnızca
          görüntüleyebilirsiniz.
        </p>
      )}

      {hesabiEtkiler && data.engelleyenEksikler.length > 0 && (
        <div role="alert" className="rounded-alan bg-hata-500/10 text-notr-800 p-3 text-sm">
          <strong>Kullanıma devam etmek için gereken onaylar eksik:</strong>
          <ul className="mt-1 list-inside list-disc">
            {data.engelleyenEksikler.map((t) => (
              <li key={t}>{RIZA_ADI[t]}</li>
            ))}
          </ul>
          {/*
            Listede olmayan onaylarin da eksik olabilecegini SOYLUYOR.
            Aksi halde bu kutuyu okuyan kisi, asagida gordugu her
            "Onaysiz" satirini de hesabi engelliyor saniyordu ve
            gereksiz yere hepsini onaylamaya calisiyordu — istege
            bagli bir riza, gerekli sanildigi icin verilmis olur ki
            KVKK anlaminda o riza SERBEST IRADE ile verilmis sayilmaz.
          */}
          <p className="text-notr-600 mt-1.5 text-xs">
            Aşağıdaki listede bunların dışında “Onaysız” satırlar da olabilir; onlar hesabı
            engellemiyor.
          </p>
        </div>
      )}

      {turler.length === 0 ? (
        <BosDurum
          baslik="Yayında metin yok"
          aciklama="Onay alınabilmesi için önce metinlerin yayınlanması gerekiyor."
        />
      ) : (
        <>
          <ul className="flex flex-col gap-2">{zorunluOlanlar.map(satir)}</ul>

          {/*
            ================================================================
            AYRI KUTU, AYRI DİL — VE SEBEBİ HUKUKİ
            ================================================================
            Tuval bunu istiyor: *"İSTEĞE BAĞLI: aynı listede DEĞİL, ayrı
            kutu, ayrı dil."*

            Ayırmanın sebebi kozmetik değil: **gerekli sanıldığı için
            verilen bir açık rıza, KVKK anlamında serbest irade ile
            verilmiş sayılmaz.** Karışık bir liste yalnızca kafa
            karıştırmıyor, topladığı rızayı hukuken sakatlıyor.

            Satır içi cümleler ("Onaylanmadan kullanım açılmaz" /
            "İsteğe bağlı…") KALDI: kutu başlığı sayfayı tarayan kişiye
            yetiyor, ama tek bir satıra odaklanan ya da ekran
            okuyucuyla gezen kişi kutu başlığını çoktan geçmiş oluyor.

            Kutu yalnızca isteğe bağlı rıza VARSA çiziliyor — boş bir
            başlık, olmayan bir şeye yer ayırmak olurdu.
          */}
          {istegeBagliOlanlar.length > 0 && (
            <section className="rounded-kart border-notr-200 bg-notr-50 mt-2 flex flex-col gap-2 border p-3">
              <div>
                <h3 className="text-notr-900 text-sm font-semibold">
                  İsteğe bağlı — hesabı etkilemiyor
                </h3>
                <p className="text-notr-600 text-xs">
                  Bunları vermeseniz de hizmet aynen sürer. Verilmemesinin tek sonucu, karşısında
                  yazan kanalın kullanılmamasıdır.
                </p>
              </div>
              <ul className="flex flex-col gap-2">{istegeBagliOlanlar.map(satir)}</ul>
            </section>
          )}
        </>
      )}

      {(verMut.isError || cekMut.isError) && (
        <p role="alert" className="text-hata-500 text-sm">
          {(verMut.error ?? cekMut.error) instanceof ApiHatasi
            ? ((verMut.error ?? cekMut.error) as ApiHatasi).message
            : "İşlem tamamlanamadı."}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// BASVURULAR — KVKK m.13, 30 gun
// ---------------------------------------------------------------

/**
 * Formdaki seçenek metinleri, listedeki `BASVURU_ADI`dan AYRI.
 *
 * Liste kısa etiket istiyor ("Silme"); seçim yapan kişi ise ne
 * istediğini kendi cümlesiyle görmeli. Aynı sözlüğü iki yerde
 * kullansaydık biri her zaman yanlış tonda olurdu.
 */
const BASVURU_SECENEGI: Record<BasvuruTuru, string> = {
  ACCESS: "Verilerime erişmek istiyorum",
  RECTIFICATION: "Yanlış bilgimin düzeltilmesini istiyorum",
  ERASURE: "Verilerimin silinmesini istiyorum",
  PORTABILITY: "Verilerimin bir kopyasını istiyorum",
  OBJECTION: "Bir işleme itiraz ediyorum",
  RESTRICTION: "İşlemenin sınırlandırılmasını istiyorum",
};

/**
 * BAŞVURU AÇMA FORMU — KVKK m.11/13
 *
 * `POST /kvkk/basvurular` yazılıydı ama hiçbir ekran çağırmıyordu:
 * başvurular LİSTELENİYOR ama AÇILAMIYORDU. Kanunun verdiği hakkı
 * kullanmanın panelde yolu yoktu.
 *
 * Uç `@KimlikGerekli()` — izin aranmıyor, çünkü bu bir yetki değil
 * HAK. Herkes kendi verisi için açabilir; ekranda da rol ayrımı yok.
 *
 * `hedefUserId` BİLEREK GÖNDERİLMİYOR: sunucu boş bırakılınca isteği
 * yapanın kendisi sayıyor. Ekranda alan açsaydık, başkasının kimliğini
 * yazıp onun adına başvuru açma denemesi mümkün olurdu — sunucu zaten
 * reddeder ama denenecek bir alan bırakmamak daha iyi. Velinin kendi
 * çocuğu adına açması ayrı bir akış (öğrenci uygulaması, Faz 6).
 */
function YeniBasvuru({ onKapat }: { onKapat: () => void }) {
  const istemci = useQueryClient();
  const [tur, setTur] = useState<BasvuruTuru>("ACCESS");
  const [ayrinti, setAyrinti] = useState("");
  const [sonucBilgi, setSonucBilgi] = useState<{ dueAt: string } | null>(null);

  const mut = useMutation({
    mutationFn: () => basvuruAc({ tur, ayrinti: ayrinti.trim() }),
    onSuccess: async (c) => {
      setSonucBilgi({ dueAt: c.dueAt });
      await istemci.invalidateQueries({ queryKey: ["kvkk-basvurular"] });
    },
  });

  if (sonucBilgi) {
    return (
      <Kart baslik="Başvurunuz alındı">
        <div className="flex flex-col gap-3">
          <p className="text-notr-700 text-sm">
            Başvurunuz kaydedildi. Kanunen en geç <strong>{tarih(sonucBilgi.dueAt)}</strong>{" "}
            tarihine kadar yanıtlanması gerekiyor.
          </p>
          <div>
            <Buton onClick={onKapat}>Listeye dön</Buton>
          </div>
        </div>
      </Kart>
    );
  }

  return (
    <Kart
      baslik="Veri sahibi başvurusu"
      aciklama="KVKK m.11 kapsamındaki haklarınız. Başvurunuz 30 gün içinde yanıtlanır."
    >
      <div className="flex flex-col gap-4">
        <Secim
          etiket="Talebiniz"
          value={tur}
          onChange={(e) => setTur(e.target.value as BasvuruTuru)}
        >
          {(Object.keys(BASVURU_SECENEGI) as BasvuruTuru[]).map((t) => (
            <option key={t} value={t}>
              {BASVURU_SECENEGI[t]}
            </option>
          ))}
        </Secim>

        <MetinAlani
          etiket="Açıklama"
          rows={5}
          value={ayrinti}
          onChange={(e) => setAyrinti(e.target.value)}
          yardim="En az 10 karakter. Hangi veriyle ilgili olduğunu yazarsanız daha hızlı sonuçlanır."
        />

        {mut.isError && (
          <p role="alert" className="text-hata-700 text-sm font-medium">
            {mut.error instanceof ApiHatasi ? mut.error.message : "Başvuru açılamadı."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton
            yukleniyor={mut.isPending}
            disabled={ayrinti.trim().length < 10}
            onClick={() => mut.mutate()}
          >
            Başvuruyu gönder
          </Buton>
          <Buton gorunum="ikincil" onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </div>
    </Kart>
  );
}

function Basvurular({ sonuclandirabilir }: { sonuclandirabilir: boolean }) {
  const istemci = useQueryClient();
  const [acik, setAcik] = useState<string | null>(null);
  const [sonuc, setSonuc] = useState("");
  const [yeniAcik, setYeniAcik] = useState(false);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["kvkk-basvurular"],
    queryFn: () => basvurular(),
  });

  const mut = useMutation({
    mutationFn: (v: { id: string; durum: "COMPLETED" | "REJECTED" }) =>
      basvuruSonuclandir(v.id, { durum: v.durum, sonuc: sonuc.trim() }),
    onSuccess: async () => {
      setAcik(null);
      setSonuc("");
      await istemci.invalidateQueries({ queryKey: ["kvkk-basvurular"] });
    },
  });

  if (yeniAcik) return <YeniBasvuru onKapat={() => setYeniAcik(false)} />;

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Başvurular alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/*
        Ayni uc iki farkli liste donuyor. Hangisini gordugunu YAZMAK
        gerekiyor: yonetim izni olmayan biri "basvuru yok, sistem
        calismiyor" diye dusunurdu.
      */}
      <p className="text-notr-600 text-xs">
        {data.yonetimGorunumu
          ? "Tüm başvurular gösteriliyor. KVKK m.13: başvurular 30 gün içinde sonuçlandırılmalı."
          : "Yalnızca sizinle ilgili başvurular gösteriliyor."}
      </p>

      <div className="flex justify-end">
        <Buton onClick={() => setYeniAcik(true)}>Başvuru aç</Buton>
      </div>

      {data.basvurular.length === 0 ? (
        <BosDurum
          baslik="Başvuru yok"
          aciklama="KVKK m.11 kapsamındaki haklarınız için başvuru açabilirsiniz."
          eylem={<Buton onClick={() => setYeniAcik(true)}>Başvuru aç</Buton>}
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {data.basvurular.map((b) => (
            <Kart olarak="li" key={b.id} vurgu={b.gecikmisMi ? "dikkat" : "yok"}>
              <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-notr-900 text-sm font-semibold">{BASVURU_ADI[b.kind]}</p>
                  <p className="text-notr-700 mt-1 text-sm whitespace-pre-wrap">{b.detail}</p>
                  <p className="text-notr-600 mt-2 text-xs">
                    {tarih(b.createdAt)} · son tarih {tarih(b.dueAt)}
                    {/*
                      Kalan gun ANLIK: zamanlanmis isin durumu
                      guncellemesini beklemek, 30 gunluk yasal sureyi
                      kacirmanin en kolay yolu olurdu.
                    */}
                    {b.status !== "COMPLETED" && b.status !== "REJECTED" && (
                      <>
                        {" · "}
                        <span className={b.gecikmisMi ? "text-hata-500 font-medium" : ""}>
                          {b.gecikmisMi
                            ? `${Math.abs(b.kalanGun)} gün GECİKTİ`
                            : `${b.kalanGun} gün kaldı`}
                        </span>
                      </>
                    )}
                  </p>
                </div>

                <Rozet className="shrink-0">{DURUM_ADI[b.status]}</Rozet>
              </div>

              {b.resolution && (
                <p className="rounded-alan bg-notr-50 text-notr-700 mt-3 p-2 text-sm">
                  <strong className="font-medium">Sonuç:</strong> {b.resolution}
                </p>
              )}

              {/* F-LGL-02 — listeyi görebilmek başvuruyu sonuçlandırma yetkisi vermez. */}
              {sonuclandirabilir && b.status !== "COMPLETED" && b.status !== "REJECTED" && (
                <div className="mt-3">
                  {acik === b.id ? (
                    <form className="flex flex-col gap-2" onSubmit={(e) => e.preventDefault()}>
                      <Alan
                        etiket="Sonuç açıklaması"
                        value={sonuc}
                        onChange={(e) => setSonuc(e.target.value)}
                        yardim="Başvurana iletilecek metin. KVKK gereği gerekçe yazılmalı."
                      />
                      <div className="flex gap-2">
                        <Buton
                          onClick={() => mut.mutate({ id: b.id, durum: "COMPLETED" })}
                          disabled={sonuc.trim().length < 5 || mut.isPending}
                        >
                          Sonuçlandır
                        </Buton>
                        <Buton
                          gorunum="sessiz"
                          onClick={() => mut.mutate({ id: b.id, durum: "REJECTED" })}
                          disabled={sonuc.trim().length < 5 || mut.isPending}
                        >
                          Reddet
                        </Buton>
                        <Buton gorunum="sessiz" onClick={() => setAcik(null)}>
                          Vazgeç
                        </Buton>
                      </div>
                    </form>
                  ) : (
                    <Buton gorunum="ikincil" onClick={() => setAcik(b.id)}>
                      Karara bağla
                    </Buton>
                  )}
                </div>
              )}
            </Kart>
          ))}
        </ul>
      )}

      {mut.isError && (
        <p role="alert" className="text-hata-500 text-sm">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Başvuru sonuçlandırılamadı."}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// METINLER
// ---------------------------------------------------------------

/**
 * İKİ SÜRÜMÜN FARKI — F-LGL-04
 *
 * ================================================================
 * NEDEN GEREKLİ
 * ================================================================
 * Yeni aydınlatma metni yayınlanınca eski rızalar geçerliliğini
 * yitirebilir ve bu karar tek soruya bağlı: metinde ne değişti? O
 * soruyu cevaplamanın yolu bugüne kadar iki metni gözle okumaktı.
 *
 * FARK SUNUCUDA HESAPLANIYOR. İstemcide diff kütüphanesi yok ve
 * olmamalı: eski rızaların geçerliliği hukuki bir karar ve o kararın
 * dayanağı istemciye göre değişemez.
 *
 * GÖRÜNÜM SALT OKUNUR. Bu ekrandan metin düzenlenmiyor; düzenleme
 * yayın akışının kendi adımı. Fark ekranından yapılan bir değişikliğin
 * hangi sürüme gittiği belirsiz kalırdı.
 */
function MetinFarki({
  tur,
  eski,
  yeni,
  onKapat,
}: {
  tur: RizaTuru;
  eski: string;
  yeni: string;
  onKapat: () => void;
}) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["metin-fark", tur, eski, yeni],
    queryFn: () => metinKarsilastir(tur, eski, yeni),
    retry: false,
  });

  return (
    <Kart
      baslik={`${RIZA_ADI[tur]} · ${eski} → ${yeni}`}
      aciklama="Salt okunur karşılaştırma. Metin bu ekrandan düzenlenmez."
      eylem={
        <Buton gorunum="ikincil" onClick={onKapat}>
          Kapat
        </Buton>
      }
    >
      {isPending ? (
        <Iskelet satir={5} />
      ) : isError ? (
        /*
          SUNUCUNUN MESAJI AYNEN. `SURUM_YOK` hatası hangi sürümün
          bulunamadığını yazıyor; kendi cümlemizi koyarsak o bilgi
          kaybolur ve kullanıcı hangi sürümü aradığını bilemez.
        */
        <BosDurum
          ton="hata"
          baslik="Karşılaştırma alınamadı"
          aciklama={error instanceof ApiHatasi ? error.message : undefined}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {/*
            BAŞLIK DEĞİŞİKLİĞİ AYRI GÖSTERİLİYOR. Başlık gövde farkının
            İÇİNDE görünmüyor (ayrı bir alan); değişmiş bir başlığı
            kaçırmak, metnin kapsamının değiştiğini kaçırmaktır.
          */}
          {data.baslikDegisti && (
            <p className="rounded-alan bg-uyari-500/10 text-notr-800 px-3 py-2 text-sm">
              <strong>Başlık değişti:</strong> “{data.eski.baslik}” → “{data.yeni.baslik}”
            </p>
          )}

          {data.ozet.aynilar ? (
            /*
              "FARK YOK" AÇIKÇA YAZILIYOR. Boş bir fark listesi
              "yüklenmedi" ile karışır ve okuyan metnin değiştiğini
              sanıp bakmaya devam eder.
            */
            <p role="status" className="rounded-alan bg-notr-50 text-notr-700 px-3 py-2 text-sm">
              İki sürümün gövdesi <strong>birebir aynı</strong>.
              {data.baslikDegisti ? " Yalnızca başlık değişmiş." : " Hiçbir satır değişmemiş."}
            </p>
          ) : (
            <>
              <p className="text-notr-600 text-sm">
                <span className="text-onay-700 font-medium">{data.ozet.eklenen} satır eklendi</span>
                {" · "}
                <span className="text-hata-700 font-medium">{data.ozet.silinen} satır silindi</span>
              </p>

              {/*
                DEĞİŞEN SATIR, SILINDI + EKLENDI ÇİFTİ olarak geliyor —
                kelime bazlı değil ve bu bilinçli: "veriler silinir" →
                "veriler silinmez" farkında kelime farkı yalnızca bir
                eki işaretler ve göz onu kaçırır.

                BAŞTAKİ BOŞLUK ANLAMLI: girinti madde yapısını taşıyor.
                `trim` yok, `whitespace-pre-wrap` var.
              */}
              <ol className="rounded-alan border-notr-200 overflow-x-auto border">
                {data.satirlar.map((s, i) => (
                  <li
                    key={`${s.tur}-${s.eskiSatir ?? "y"}-${s.yeniSatir ?? "e"}-${i}`}
                    className={cn(
                      "flex gap-3 px-3 py-1 font-mono text-xs",
                      s.tur === "EKLENDI" && "bg-basarili-500/10",
                      s.tur === "SILINDI" && "bg-hata-500/10",
                    )}
                  >
                    <span className="text-notr-400 w-10 shrink-0 text-right tabular-nums select-none">
                      {s.eskiSatir ?? ""}
                    </span>
                    <span className="text-notr-400 w-10 shrink-0 text-right tabular-nums select-none">
                      {s.yeniSatir ?? ""}
                    </span>
                    {/*
                      İŞARET RENKTEN AYRI: renk tek başına anlam
                      taşımaz. "+"/"−" ekran okuyucuda da okunuyor.
                    */}
                    <span
                      className={cn(
                        "w-3 shrink-0 font-semibold select-none",
                        s.tur === "EKLENDI" && "text-onay-700",
                        s.tur === "SILINDI" && "text-hata-700",
                        s.tur === "AYNI" && "text-notr-300",
                      )}
                    >
                      {s.tur === "EKLENDI" ? "+" : s.tur === "SILINDI" ? "−" : " "}
                    </span>
                    <span className="text-notr-800 break-words whitespace-pre-wrap">{s.metin}</span>
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
      )}
    </Kart>
  );
}

function Metinler() {
  const istemci = useQueryClient();
  const [formAcik, setFormAcik] = useState(false);
  /** Açık karşılaştırma — F-LGL-04. */
  const [fark, setFark] = useState<{ tur: RizaTuru; eski: string; yeni: string } | null>(null);
  const [tur, setTur] = useState<RizaTuru>("PRIVACY_NOTICE");
  const [surum, setSurum] = useState("");
  const [baslik, setBaslik] = useState("");
  const [govde, setGovde] = useState("");

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["kvkk-metinler"],
    queryFn: () => metinSurumleri(),
  });

  const mut = useMutation({
    mutationFn: () =>
      metinYayinla({ tur, surum: surum.trim(), baslik: baslik.trim(), govde: govde.trim() }),
    onSuccess: async () => {
      setSurum("");
      setBaslik("");
      setGovde("");
      setFormAcik(false);
      await istemci.invalidateQueries({ queryKey: ["kvkk-metinler"] });
      await istemci.invalidateQueries({ queryKey: ["rizalarim"] });
    },
  });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Metinler alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  const eksik = surum.trim() === "" || baslik.trim().length < 3 || govde.trim().length < 20;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Buton onClick={() => setFormAcik((a) => !a)} aria-expanded={formAcik}>
          {formAcik ? "Formu kapat" : "Yeni sürüm yayınla"}
        </Buton>
      </div>

      {formAcik && (
        <form
          className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-3 border p-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!eksik) mut.mutate();
          }}
        >
          {/*
            ================================================================
            YAYINDAKI SURUM DEGISTIRILMIYOR — YENISI YAYINLANIYOR
            ================================================================
            Formun "duzenle" degil "yeni surum" olmasi bilincli.
            Duzenleme olsaydi "kullanici neyi onayladi" sorusu
            cevapsiz kalirdi: onay bir surume isaret eder ama o
            surumun icerigi sonradan degismis olurdu.
          */}
          <p className="rounded-alan bg-uyari-500/10 text-notr-800 p-3 text-xs">
            Yayındaki metin <strong>değiştirilemez</strong>. Yeni sürüm yayınlandığında bu türün{" "}
            <strong>tüm onayları eskimiş sayılır</strong> ve kullanıcılardan yeniden onay istenir.
          </p>

          <Secim
            etiket="Metin türü"
            value={tur}
            onChange={(e) => setTur(e.target.value as RizaTuru)}
          >
            {(Object.keys(RIZA_ADI) as RizaTuru[]).map((t) => (
              <option key={t} value={t}>
                {RIZA_ADI[t]}
              </option>
            ))}
          </Secim>

          <Alan
            etiket="Sürüm"
            value={surum}
            onChange={(e) => setSurum(e.target.value)}
            placeholder="v2"
            yardim="Aynı türde aynı sürüm iki kez yayınlanamaz."
          />
          <Alan etiket="Başlık" value={baslik} onChange={(e) => setBaslik(e.target.value)} />

          <MetinAlani
            etiket="Metin"
            value={govde}
            onChange={(e) => setGovde(e.target.value)}
            rows={10}
          />

          <div className="flex gap-2">
            <Buton type="submit" disabled={eksik || mut.isPending}>
              {mut.isPending ? "Yayınlanıyor…" : "Yayınla"}
            </Buton>
            <Buton gorunum="sessiz" type="button" onClick={() => setFormAcik(false)}>
              Vazgeç
            </Buton>
          </div>

          {mut.isError && (
            <p role="alert" className="text-hata-500 text-sm">
              {mut.error instanceof ApiHatasi ? mut.error.message : "Metin yayınlanamadı."}
            </p>
          )}
        </form>
      )}

      {/*
        FARK PANELİ TABLONUN ÜSTÜNDE. Karşılaştırmayı açan kişi onu
        okumak istiyor; tablonun altına koysaydık uzun sürüm listelerinde
        ekran dışında açılır ve "düğme çalışmadı" diye okunurdu.
      */}
      {fark && (
        <MetinFarki
          tur={fark.tur}
          eski={fark.eski}
          yeni={fark.yeni}
          onKapat={() => setFark(null)}
        />
      )}

      {data.length === 0 ? (
        <BosDurum baslik="Metin yok" aciklama="Onay alınabilmesi için önce metin yayınlanmalı." />
      ) : (
        <Kart className="overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">Hukuki metin sürümleri</caption>
            <thead>
              <tr className="border-notr-200 text-notr-600 border-b text-left text-xs">
                <th scope="col" className="p-3 font-medium">
                  Tür
                </th>
                <th scope="col" className="p-3 font-medium">
                  Sürüm
                </th>
                <th scope="col" className="p-3 font-medium">
                  Başlık
                </th>
                <th scope="col" className="p-3 font-medium">
                  Durum
                </th>
                <th scope="col" className="p-3 font-medium">
                  Karşılaştır
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((m) => {
                const yayinda = m.publishedAt !== null && m.supersededAt === null;
                /*
                  ÖNCEKİ SÜRÜM `createdAt` ILE BULUNUYOR, `version` ile
                  DEĞİL: sürüm bir DİZGİ ("v1", "1.0", "2026-08" olabilir)
                  ve dizgi sıralaması "v10" < "v9" der. Yanlış sürümle
                  karşılaştırmak, hukuki bir kararı yanlış dayanağa
                  bağlamak olurdu.
                */
                const onceki = data
                  .filter((o) => o.type === m.type && o.createdAt < m.createdAt)
                  .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
                return (
                  <tr key={m.id} className="border-notr-100 border-b last:border-0">
                    <td className="text-notr-900 p-3">{RIZA_ADI[m.type]}</td>
                    <td className="text-notr-700 p-3 font-mono text-xs">{m.version}</td>
                    <td className="text-notr-700 p-3">{m.title}</td>
                    <td className="p-3">
                      <span
                        className={cn(
                          "rounded-alan px-2.5 py-0.5 text-xs font-medium",
                          yayinda
                            ? "bg-basarili-500/15 text-onay-700"
                            : "bg-notr-100 text-notr-600",
                        )}
                      >
                        {yayinda ? "Yayında" : m.supersededAt ? "Arşiv" : "Taslak"}
                      </span>
                    </td>
                    <td className="p-3">
                      {/*
                        ÖNCEKİ SÜRÜM YOKSA DÜĞME HİÇ ÇİZİLMİYOR.
                        Karşılaştıracak bir şey yokken düğme göstermek,
                        aynı gün üç kez düzeltilen "olmayan yola davet"
                        hatası olurdu.
                      */}
                      {onceki ? (
                        <Buton
                          gorunum="sessiz"
                          onClick={() =>
                            setFark({ tur: m.type, eski: onceki.version, yeni: m.version })
                          }
                        >
                          {onceki.version} ile karşılaştır
                          <span className="sr-only"> — {RIZA_ADI[m.type]}</span>
                        </Buton>
                      ) : (
                        <span className="text-notr-400 text-xs">ilk sürüm</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Kart>
      )}
    </div>
  );
}
